/**
 * ShiftCountsStrip — Feature 2 (G-34 read side)
 *
 * Sits under KPI Analytics' By Shift chart: one row per crew with its measured
 * and flagged counts for the period, and a "Show N flagged" button that opens
 * that crew's flagged results (`?shift=<id>&status=flagged`).
 *
 * The counts are the By Shift groups (measured results only, like the chart),
 * which the page never filters by crew, so with no crew selected the measured
 * counts add up to the measured card. Row order: every active crew in lookup
 * order (one with no results reads 0, so a crew the templates never sample
 * stays visible), then crews missing from the lookup that still have results
 * (inactive ones), then Unattributed, which has no shift to open.
 */

import { ArrowRight } from 'lucide-react';
import type { KpiAnalyticsGroup, ShiftLookup } from '@guild/shared';
import { cn } from '../../../lib/utils';

interface ShiftCountRow {
  /** The shift id; null for Unattributed (results with no shift). */
  id: string | null;
  name: string;
  measured: number;
  flagged: number;
}

/**
 * The strip's rows. Pure and self-contained: the API suite evaluates it from
 * this file's text (apps/api/src/__tests__/kpi-analytics-shift-filter.test.ts),
 * so it must not use anything imported.
 */
function shiftCountRows(
  groups: readonly KpiAnalyticsGroup[],
  shifts: readonly ShiftLookup[]
): ShiftCountRow[] {
  const counts = (group: KpiAnalyticsGroup | undefined) => ({
    measured: group?.count ?? 0,
    flagged: group ? group.warningCount + group.criticalCount : 0,
  });
  const listed = new Set(shifts.map((shift) => shift.id));

  const crews = shifts.map((shift) => ({
    id: shift.id,
    name: shift.name,
    ...counts(groups.find((group) => group.id === shift.id)),
  }));
  const unlisted = groups.flatMap((group) =>
    typeof group.id === 'string' && !listed.has(group.id)
      ? [{ id: group.id, name: group.key ?? 'Unknown shift', ...counts(group) }]
      : []
  );
  const unattributed = groups.flatMap((group) =>
    group.id === null ? [{ id: null, name: group.key ?? 'Unattributed', ...counts(group) }] : []
  );
  return [...crews, ...unlisted, ...unattributed];
}

interface ShiftCountsStripProps {
  /** The By Shift groups (unfiltered); undefined while they load. */
  groups: readonly KpiAnalyticsGroup[] | undefined;
  /** Active shifts from /api/shifts/lookup, in order; undefined while they load. */
  shifts: readonly ShiftLookup[] | undefined;
  /** The crew filter (`?shift=`), highlighted; null for none. */
  activeShiftId: string | null;
  onShowFlagged: (shiftId: string) => void;
}

export function ShiftCountsStrip({
  groups,
  shifts,
  activeShiftId,
  onShowFlagged,
}: ShiftCountsStripProps) {
  // A group with no `id` comes from an API older than this page (a promote runs
  // the new web against it for a few minutes). Its counts cannot be matched to
  // crews, so the strip stays hidden rather than showing every crew at 0.
  if (!groups || !shifts || groups.some((group) => group.id === undefined)) return null;
  const rows = shiftCountRows(groups, shifts);
  if (rows.length === 0) return null;

  return (
    <section
      aria-labelledby="shift-counts-heading"
      className="rounded-xl border border-gray-200 bg-white"
    >
      <div className="border-b border-gray-100 px-6 py-4">
        <h3 id="shift-counts-heading" className="font-semibold text-gray-900">
          Results by crew
        </h3>
        <p className="text-sm text-gray-500">
          Measured and flagged results in this period. Not-observed results are not counted
          per crew.
        </p>
      </div>
      <ul className="divide-y divide-gray-100">
        {rows.map(({ id, name, measured, flagged }) => {
          const active = id !== null && id === activeShiftId;
          return (
            <li
              key={id ?? 'unattributed'}
              aria-current={active || undefined}
              className={cn(
                'flex flex-wrap items-center gap-x-6 gap-y-1 px-6 py-3',
                active && 'bg-guild-mint/30'
              )}
            >
              <span className="min-w-32 font-medium text-gray-900">{name}</span>
              <span className="text-sm tabular-nums text-gray-600">
                {measured} measured · {flagged} flagged
              </span>
              {id !== null && flagged > 0 && (
                <button
                  type="button"
                  onClick={() => onShowFlagged(id)}
                  aria-label={`Show ${flagged} flagged ${flagged === 1 ? 'result' : 'results'} for ${name}`}
                  className="ml-auto inline-flex items-center gap-1 text-sm font-medium text-guild-green hover:text-guild-green/80"
                >
                  Show {flagged} flagged
                  <ArrowRight size={14} />
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export default ShiftCountsStrip;
